import React, { useState } from 'react';
import { deleteProjectAndData } from '../../api';

function DeleteProjectDataModal({ isOpen, onClose, user, teamId, teamName, onProjectDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const handleDeleteProjectAndData = async () => {
    if (!window.confirm('Sind Sie sicher, dass Sie das Projekt und alle Daten löschen möchten? Diese Aktion kann nicht rückgängig gemacht werden.')) {
      return;
    }

    setDeleting(true);
    setError('');
    setSuccessMessage('');

    try {
      const response = await deleteProjectAndData(user.id, teamId);
      
      if (response.success) {
        // Excel-Datei mit gelöschten Daten herunterladen
        if (response.deletionExcelData && response.deletionFileName) {
          try {
            // Base64-Daten dekodieren
            const binaryString = atob(response.deletionExcelData);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
              bytes[i] = binaryString.charCodeAt(i);
            }
            
            const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);
            link.setAttribute('href', url);
            link.setAttribute('download', response.deletionFileName);
            
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            
            setSuccessMessage(`Projekt gelöscht! Datei mit gelöschten Daten wurde heruntergeladen: ${response.deletionFileName}`);
          } catch (downloadError) {
            console.error('Fehler beim Herunterladen der Excel-Datei:', downloadError);
            setSuccessMessage('Projekt gelöscht! (Hinweis: Excel-Datei konnte nicht heruntergeladen werden)');
          }
        } else {
          setSuccessMessage('Projekt und alle Daten wurden erfolgreich gelöscht!');
        }
        
        // Löschstatistiken in der Console ausgeben
        if (response.deletion_stats) {
          console.log('=== Projekt-Löschstatistiken ===');
          console.log(JSON.stringify(response.deletion_stats, null, 2));
          console.log('================================');
        }
        
        if (onProjectDeleted) {
          onProjectDeleted();
        }
        setTimeout(() => {
          onClose();
        }, 3000);
      } else {
        setError(response.error || 'Fehler beim Löschen des Projekts');
      }
    } catch (err) {
      setError('Netzwerkfehler: ' + err.message);
    } finally {
      setDeleting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" style={{ 
      position: 'fixed', 
      top: 0, 
      left: 0, 
      width: '100vw', 
      height: '100vh', 
      background: 'rgba(0,0,0,0.5)', 
      zIndex: 1001, 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center' 
    }}>
      <div className="modal-content" style={{ 
        background: 'white', 
        padding: '32px', 
        borderRadius: '8px', 
        minWidth: '400px', 
        maxWidth: '600px',
        width: '90%',
        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
        maxHeight: '90vh',
        overflow: 'auto'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0 }}>Projektdaten löschen</h2>
          <button 
            onClick={onClose}
            disabled={deleting}
            style={{
              background: 'none',
              border: 'none',
              cursor: deleting ? 'not-allowed' : 'pointer',
              fontSize: '24px',
              color: '#666',
              padding: '0',
              width: '32px',
              height: '32px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>

        {error && (
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#fee',
            color: '#c33',
            borderRadius: '4px',
            border: '1px solid #fcc'
          }}>
            {error}
          </div>
        )}

        {successMessage && (
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: '#efe',
            color: '#3c3',
            borderRadius: '4px',
            border: '1px solid #cfc'
          }}>
            {successMessage}
          </div>
        )}

        <div style={{ marginBottom: '24px' }}>
          <p style={{ marginBottom: '16px', fontSize: '16px' }}>
            Das Projekt <strong>{teamName}</strong> wurde erfolgreich exportiert.
          </p>
          <p style={{ marginBottom: '16px', fontSize: '14px', color: '#666' }}>
            Möchten Sie jetzt das Projekt und alle zugehörigen Daten löschen?
          </p>
        </div>

        <div style={{ 
          padding: '16px', 
          backgroundColor: '#fff3cd', 
          borderRadius: '4px',
          marginBottom: '20px',
          border: '1px solid #ffc107'
        }}>
          <p style={{ margin: '0 0 8px 0', fontWeight: '500', color: '#856404' }}>⚠️ Wichtiger Hinweis:</p>
          <ul style={{ margin: '0', paddingLeft: '20px', color: '#856404', fontSize: '14px' }}>
            <li>Diese Aktion kann nicht rückgängig gemacht werden</li>
            <li>Stellen Sie sicher, dass Sie die exportierte Datei gespeichert haben</li>
            <li>Beim Löschen wird automatisch eine Excel-Datei mit allen gelöschten Daten erstellt und heruntergeladen</li>
            <li>Beim Löschen werden alle zugehörigen Benutzer einem anderen Projekt zugeordnet</li>
            <li>Alle Daten (Kunden, Leads, Termine, Kommentare, Anrufe) werden gelöscht</li>
          </ul>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            onClick={handleDeleteProjectAndData}
            disabled={deleting}
            style={{
              padding: '12px 24px',
              fontSize: '16px',
              border: 'none',
              borderRadius: '4px',
              backgroundColor: deleting ? '#ccc' : '#f44336',
              color: 'white',
              cursor: deleting ? 'not-allowed' : 'pointer',
              fontWeight: '500',
              width: '100%'
            }}
          >
            {deleting ? 'Lösche...' : 'Projekt und alle Daten löschen'}
          </button>

          <button
            onClick={onClose}
            disabled={deleting}
            style={{
              padding: '10px 20px',
              fontSize: '16px',
              border: '1px solid #ddd',
              borderRadius: '4px',
              backgroundColor: '#fff',
              cursor: deleting ? 'not-allowed' : 'pointer',
              color: '#666',
              fontWeight: '500'
            }}
          >
            Abbrechen
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeleteProjectDataModal;

